import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Col, Row, Tooltip, Typography,
} from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { SyncOutlined } from '@ant-design/icons';
import { estimatedEtaPropTypes } from '../../../types';
import { DEFAULT_DATE_TIME } from '../../../utils/constants/timeFormats';
import { resolveStatus, StopEtaStatusDisplay } from './StopEtaStatusLabel';
import EtaDetailsModal from './StopEtaDetailsModal';

const StopEta = ({
  etas, scheduledArrival, calculateEta, loading, disabled, tooltipKey,
}) => {
  const { t } = useTranslation();
  const [visibleModalId, setVisibleModalId] = useState(null);

  const showModal = (vehicleId) => setVisibleModalId(vehicleId);
  const handleCancel = () => setVisibleModalId(null);

  const resolveArrivalStatus = (eta) => {
    if (!etas || !scheduledArrival) {
      return <></>;
    }
    const arrivalStatus = resolveStatus(scheduledArrival, eta.eta.arrival);
    return (
      <StopEtaStatusDisplay style={{ marginLeft: '6px' }} status={arrivalStatus}>
        {t(`ORDER.STOP_ETA_STATUS.${arrivalStatus}`)}
      </StopEtaStatusDisplay>
    );
  };

  const viewTimeToEnd = (seconds) => {
    let totalSeconds = seconds;
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    return hours === 0 ? `${minutes}min` : `${hours}h ${minutes}min`;
  };

  const viewLengthToEndInKm = (meters) => `${Math.floor(meters / 1000)}km left`;

  return (
    <Row justify="end" style={{ textAlign: 'right' }}>
      <Col style={{ textAlign: 'right' }}>
        <div style={{ marginBottom: '8px' }}>
          {calculateEta && (
            <Tooltip title={tooltipKey && t(tooltipKey)} placement="topRight">
              <Button
                size="small"
                style={{ padding: 0, margin: 0 }}
                type="link"
                onClick={calculateEta}
                icon={(etas || loading) && <SyncOutlined />}
                loading={loading}
                disabled={disabled || loading}
              >
                {etas ? t('ORDER.REFRESH_ETA') : t('ORDER.CALCULATE_ETA')}
              </Button>
            </Tooltip>
          )}
        </div>
        {calculateEta && etas && etas.map((eta) => (
          <div key={eta.vehicleId} style={{ marginBottom: '8px' }}>
            <p style={{ margin: 0 }}>
              <Typography.Text strong>
                {eta.licensePlateNumber}
              </Typography.Text>
            </p>
            <p style={{ margin: 0 }}>{moment.unix(eta.eta.arrival).format(DEFAULT_DATE_TIME)}</p>
            <p style={{ margin: 0, opacity: 0.6 }}>
              {`${viewTimeToEnd(eta.eta.duration)}, ${viewLengthToEndInKm(eta.eta.length)}`}
            </p>
            {resolveArrivalStatus(eta)}
            {eta.eta?.details && (
              <div>
                <Button
                  type="link"
                  onClick={() => showModal(eta.vehicleId)}
                  style={{ padding: 0 }}
                >
                  {t('ORDER.STOP.ETA.SHOW_MORE')}
                </Button>
              </div>
            )}

            <EtaDetailsModal
              visible={visibleModalId === eta.vehicleId}
              onClose={handleCancel}
              vehicleEta={eta}
            />
          </div>
        ))}
      </Col>
    </Row>
  );
};

StopEta.propTypes = {
  etas: PropTypes.arrayOf(estimatedEtaPropTypes),
  calculateEta: PropTypes.func,
  loading: PropTypes.bool,
  scheduledArrival: PropTypes.number,
  tooltipKey: PropTypes.string,
  disabled: PropTypes.bool,
};

StopEta.defaultProps = {
  etas: null,
  calculateEta: null,
  loading: false,
  scheduledArrival: null,
  tooltipKey: null,
  disabled: false,
};

export default StopEta;
